import * as THREE from 'three';
import { PhysicsEngine } from '../physics/PhysicsEngine';
import {
  createTrackedBodyVisualSet,
  type TrackedBodyVisualSet,
} from './trackedBodyVisuals';
import type { TrackedSpringVisualSet } from './trackedSpringVisuals';
import {
  COLISEUM_CAP_HALF_EXTENTS,
  COLISEUM_SUPPORT_HALF_EXTENTS,
  generateColiseumLayout,
  type ColiseumDemoVariant,
} from './coliseumLayout';

export type { ColiseumDemoVariant } from './coliseumLayout';

export type ColiseumDemoResult = {
  trackedVisualSets: TrackedBodyVisualSet[];
  trackedSpringVisualSets: TrackedSpringVisualSet[];
  brickCount: number;
  shootProjectile: (config: {
    position: [number, number, number];
    direction: [number, number, number];
    averageSpeed: number;
    speedJitter?: number;
    angularJitter?: number;
  }) => boolean;
};

const BRICK_MASS = 0.5;
const BRICK_FRICTION = 0.72;
const PROJECTILE_RADIUS = 2.0;
const PROJECTILE_MASS = 240.0;
const PROJECTILE_FRICTION = 0.55;
const PROJECTILE_CAPACITY = 96;

export function buildColiseumDemo(args: {
  scene: THREE.Object3D;
  physics: PhysicsEngine;
  variant: ColiseumDemoVariant;
}): ColiseumDemoResult {
  const { scene, physics, variant } = args;
  const trackedVisualSets: TrackedBodyVisualSet[] = [];
  const trackedSpringVisualSets: TrackedSpringVisualSet[] = [];
  const layout = generateColiseumLayout(variant, physics.getCollisionMargin());
  const brickCount = layout.brickCount;

  const supportVisuals = createTrackedBodyVisualSet(scene, physics, {
    capacity: layout.supportBrickCount,
    halfExtents: [...COLISEUM_SUPPORT_HALF_EXTENTS],
    color: 0xbd7b3b,
    materialModel: 'lambert',
    shaderDrivenInstancing: true,
    showOutline: false,
    castShadow: true,
    receiveShadow: true,
  });
  const capVisuals = createTrackedBodyVisualSet(scene, physics, {
    capacity: layout.capBrickCount,
    halfExtents: [...COLISEUM_CAP_HALF_EXTENTS],
    color: 0xbd7b3b,
    materialModel: 'lambert',
    shaderDrivenInstancing: true,
    showOutline: false,
    castShadow: true,
    receiveShadow: true,
  });
  trackedVisualSets.push(supportVisuals, capVisuals);
  const projectileVisuals = createTrackedBodyVisualSet(scene, physics, {
    capacity: PROJECTILE_CAPACITY,
    geometry: new THREE.SphereGeometry(PROJECTILE_RADIUS, 28, 20),
    disposeGeometry: true,
    color: 0x8d97a6,
    roughness: 0.52,
    metalness: 0.14,
    materialModel: 'standard',
    showOutline: false,
    castShadow: true,
    receiveShadow: true,
  });
  trackedVisualSets.push(projectileVisuals);
  let projectileCount = 0;

  const xAxis = new THREE.Vector3(1.0, 0.0, 0.0);
  const yAxis = new THREE.Vector3(0.0, 1.0, 0.0);
  const uprightQuaternion = new THREE.Quaternion().setFromAxisAngle(xAxis, Math.PI * 0.5);
  const poseQuaternion = new THREE.Quaternion();
  const position: [number, number, number] = [0.0, 0.0, 0.0];
  const quaternion: [number, number, number, number] = [0.0, 0.0, 0.0, 1.0];
  const supportHalfExtents: [number, number, number] = [...COLISEUM_SUPPORT_HALF_EXTENTS];
  const capHalfExtents: [number, number, number] = [...COLISEUM_CAP_HALF_EXTENTS];
  const bodyDescriptor = {
    position,
    halfExtents: supportHalfExtents,
    quaternion,
    mass: BRICK_MASS,
    friction: BRICK_FRICTION,
  };

  // Reuse the descriptor, pose arrays and Three math objects for all 50K/64K
  // bricks. addBody consumes them synchronously and still performs the same
  // quaternion normalization and Float32 stores in the same body order. This
  // removes tens of thousands of short-lived objects without changing a
  // single authored pose or any GPU arithmetic.
  physics.withMutationBatch(() => {
    for (const ring of layout.rings) {
      const uprightSupport = ring.kind === 'support';
      bodyDescriptor.halfExtents = uprightSupport ? supportHalfExtents : capHalfExtents;
      const visuals = uprightSupport ? supportVisuals : capVisuals;
      for (let brick = 0; brick < ring.brickCount; brick++) {
        const angle = ring.phase + brick * ring.angleStep;
        position[0] = Math.cos(angle) * ring.radius;
        position[1] = ring.y;
        position[2] = Math.sin(angle) * ring.radius;
        if (uprightSupport) {
          poseQuaternion
            .setFromAxisAngle(yAxis, -(angle + Math.PI * 0.5))
            .multiply(uprightQuaternion);
        } else {
          poseQuaternion.setFromAxisAngle(yAxis, -angle);
        }
        quaternion[0] = poseQuaternion.x;
        quaternion[1] = poseQuaternion.y;
        quaternion[2] = poseQuaternion.z;
        quaternion[3] = poseQuaternion.w;
        const body = physics.addBody(bodyDescriptor);
        visuals.addBody(body);
      }
    }
  });

  console.log(
    `[ColiseumDemo] ${variant} built ${brickCount} bricks; `
    + `restGap=${layout.restGap.toFixed(6)}m `
    + `maxC0Error=${layout.diagnostics.maxRestC0Error.toExponential(2)} `
    + `minCapSeam=${layout.diagnostics.minCapSeamGap.toFixed(6)}m.`,
  );

  return {
    trackedVisualSets,
    trackedSpringVisualSets,
    brickCount,
    shootProjectile: (config) => {
      if (projectileCount >= PROJECTILE_CAPACITY || physics.getBodyCount() >= physics.config.maxBodies) {
        return false;
      }
      const dir = new THREE.Vector3(config.direction[0], config.direction[1], config.direction[2]);
      if (dir.lengthSq() < 1e-8) {
        return false;
      }
      dir.normalize();
      const speedJitter = Math.max(config.speedJitter ?? 0.0, 0.0);
      const averageSpeed = Math.max(config.averageSpeed, 0.0);
      const speed = Math.max(0.0, averageSpeed + THREE.MathUtils.randFloatSpread(speedJitter * 2.0));
      const velocity = dir.multiplyScalar(speed);
      const angularJitter = Math.max(config.angularJitter ?? 0.0, 0.0) * 0.25;
      const body = physics.addBody({
        position: config.position,
        shapeType: 'sphere',
        radius: PROJECTILE_RADIUS,
        mass: PROJECTILE_MASS,
        friction: PROJECTILE_FRICTION,
        linearVelocity: [velocity.x, velocity.y, velocity.z],
        angularVelocity: [
          THREE.MathUtils.randFloatSpread(angularJitter * 2.0),
          THREE.MathUtils.randFloatSpread(angularJitter * 2.0),
          THREE.MathUtils.randFloatSpread(angularJitter * 2.0),
        ],
      });
      projectileVisuals.addBody(body);
      projectileCount++;
      // The Coliseum is one large connected sleeping island. If it stays
      // asleep until the first contact, the impact frame must wake and rebuild
      // solver state for every brick at once, which presents as a collision
      // hitch. Begin that exact full-accuracy wake on the already interactive
      // launch frame so contact itself has no deferred island transition.
      physics.requestWakeAllBodies();
      return true;
    },
  };
}
